import { useCallback } from "react";
import { API } from "../utils/amplifyWrapper";
import * as Sentry from '@sentry/react';
import { toast } from "react-toastify";
import { openChargebeeCheckout, getChargebee } from "../utils/chargebee";
import { trackGA4Event, setGA4UserProperties } from "../utils/trackGA4Event";
import { getGa4PlanTier } from "../utils/ga4PlanTier";
import { PLAN_SELECTED } from "../constants/gtag-events";

function extractHostedPage(obj) {
  return obj?.hosted_page || obj?.hostedPage || obj;
}

function ensureHostedPageUrl(hostedPageObj) {
  if (!hostedPageObj?.url) {
    const err = new Error("Hosted page missing from response");
    err.hostedPageObj = hostedPageObj;
    throw err;
  }
  return hostedPageObj;
}

export function useBilling({ orgId }) {
  const validateCheckout = useCallback(async (planId) => {
    const resp = await API.post(`main`, `/organisation/${orgId}/validate_checkout`, {
      headers: { "Content-Type": "application/json" },
    });

    if (resp?.change_type) {
      // backend response doesn't include plan_id, so it has to come in from
      // whoever called startCheckout
      const planTier = getGa4PlanTier(planId);
      trackGA4Event(PLAN_SELECTED, {
        change_type: resp.change_type,
        ...(planId && { plan_id: planId }),
        plan_tier: planTier,
      });
      setGA4UserProperties({
        user_plan_tier: planTier,
        user_type: planTier === 'free' ? 'free' : 'paid',
      });
    }

    return resp;
  }, [orgId]);

  const startCheckout = useCallback(
    async ({
      hostedPageFn,
      afterValidate,
      onClose,
      layout,
      planId,
      errorMessage = "Something went wrong in checkout!",
    }) => {
      try {
        const didOpenPopup = await openChargebeeCheckout({
          hostedPage: hostedPageFn,
          onSuccess: async function () {
            try {
              await validateCheckout(planId);
            } finally {
              if (afterValidate) await afterValidate();
            }
          },
          onClose: onClose || function () {},
          layout,
        });

        if (!didOpenPopup) {
          const hostedPageObj = ensureHostedPageUrl(await hostedPageFn());
          window.open(hostedPageObj.url, "_self");
        }
      } catch (e) {
        // Keep behavior consistent across the app.
        // eslint-disable-next-line no-console
        console.log(e);
        toast.error(errorMessage);
        Sentry.captureException(e);
        throw e;
      }
    },
    [validateCheckout]
  );

  const createNewHostedPageFn = useCallback(
    ({ planId, redirectURL, cancelURL, source }) => {
      return async () => {
        const resp = await API.post(
          `main`,
          `/organisation/${orgId}/generate_checkout_hp_url`,
          {
            body: { plan_id: planId, redirectURL, cancelURL, source },
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!resp?.success) {
          throw new Error("Something went wrong in checkout!");
        }

        return ensureHostedPageUrl(extractHostedPage(resp?.data));
      };
    },
    [orgId]
  );

  const createUpgradeHostedPageFn = useCallback(
    ({ planId, redirectURL, cancelURL, source }) => {
      return async () => {
        const sourceParam = source ? `&source=${encodeURIComponent(source)}` : "";
        const resp = await API.get(
          "main",
          `/organisation/${orgId}/create-upgrade-session?planId=${encodeURIComponent(
            planId
          )}&redirectURL=${encodeURIComponent(
            redirectURL
          )}&cancelURL=${encodeURIComponent(cancelURL)}${sourceParam}`,
          { headers: { "Content-Type": "application/json" } }
        );

        if (!resp?.success) {
          throw new Error("Something went wrong in checkout!");
        }

        // Backend returns { success: true, data: hosted_page }
        return ensureHostedPageUrl(extractHostedPage(resp?.data));
      };
    },
    [orgId]
  );

  const openPortal = useCallback(
    async ({ sectionType = "ACCOUNT_DETAILS", redirectURL }) => {
      try {
        const Chargebee = await getChargebee();
        if (!Chargebee) {
          const response = await API.get(
            "main",
            `/organisation/${orgId}/create-portal-session?redirectURL=${encodeURIComponent(
              redirectURL
            )}`
          );
          window.open(response.access_url, "_self");
          return;
        }

        const chargebeeInstance = Chargebee.getInstance();
        chargebeeInstance.setPortalSession(function () {
          return API.get(
            "main",
            `/organisation/${orgId}/create-portal-session?redirectURL=${encodeURIComponent(
              redirectURL
            )}`
          );
        });

        const cbPortal = chargebeeInstance.createChargebeePortal();

        let cbSectionType;
        switch (sectionType) {
          case "BILLING_HISTORY":
            cbSectionType = Chargebee.getPortalSections().BILLING_HISTORY;
            break;
          case "PAYMENT_SOURCES":
            cbSectionType = Chargebee.getPortalSections().PAYMENT_SOURCES;
            break;
          case "EDIT_BILLING_ADDRESS":
            cbSectionType = Chargebee.getPortalSections().EDIT_BILLING_ADDRESS;
            break;
          default:
            cbSectionType = Chargebee.getPortalSections().ACCOUNT_DETAILS;
        }

        if (["EDIT_BILLING_ADDRESS"].includes(sectionType)) {
          cbPortal.open(
            {
              close: function () {
                chargebeeInstance.logout();
              },
            },
            { sectionType: cbSectionType }
          );
        } else {
          cbPortal.openSection(
            { sectionType: cbSectionType },
            {
              close: function () {
                chargebeeInstance.logout();
              },
            }
          );
        }
      } catch (e) {
        toast.error("Something went wrong");
        Sentry.captureException(e);
        throw e;
      }
    },
    [orgId]
  );

  const cancelSubscription = useCallback(
    async ({ cancelReason, cancelSuggestion }) => {
      try {
        if (!cancelReason || !cancelReason.length) {
          toast.error("Please add a cancellation reason before proceeding");
          return { success: false, skipped: true };
        }

        const resp = await API.post(`main`, `/organisation/${orgId}/cancel-subscription`, {
          body: { cancelReason, cancelSuggestion },
          headers: { "Content-Type": "application/json" },
        });

        toast.success("Your subscription is cancelled successfully");
        return resp;
      } catch (e) {
        toast.error("Something went wrong");
        Sentry.captureException(e);
        throw e;
      }
    },
    [orgId]
  );

  const resumeSubscription = useCallback(async () => {
    try {
      const resp = await API.post(`main`, `/organisation/${orgId}/resume-subscription`, {
        headers: { "Content-Type": "application/json" },
      });
      toast.success("Your subscription has been resumed");
      return resp;
    } catch (e) {
      toast.error("Something went wrong");
      Sentry.captureException(e);
      throw e;
    }
  }, [orgId]);

  return {
    validateCheckout,
    startCheckout,
    createNewHostedPageFn,
    createUpgradeHostedPageFn,
    openPortal,
    cancelSubscription,
    resumeSubscription,
  };
}

